import { Box, Typography } from "@mui/material";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { COLORS } from "../assets/theme";
import CustomDialog from "./common/CustomDialog";
import CustomTable from "./common/table/CustomTable";
import { fetchCountry } from "../services/CountryService";
import { fetchOperator } from "../services/OperatorsService";
import { fetchRoutingPlan } from "../services/RoutingPlanService";
import { IField, IPageable, Params } from "../types/table/interfaces.type";

/** Minimum shape a row must have to look up the instructions sharing its route. */
export interface RelatedInstructionsRow {
  id: string | number;
  routingPlanId?: string;
  operatorId?: number;
}

type RelatedFetchFunc<T> = (
  params: Params<T>
) => UseQueryResult<IPageable<T>, Error>;

interface RelatedInstructionsDialogProps<T> {
  open: boolean;
  onClose: () => void;
  /** Row the action was triggered from; its route is used as the filter. */
  row: RelatedInstructionsRow | null;
  title: string;
  fields: IField<T>[];
  fetchFunc: RelatedFetchFunc<T>;
}

function RouteSummary({
  row,
  total,
}: Readonly<{ row: RelatedInstructionsRow; total?: number }>) {
  const { data: routingPlan } = useQuery({
    queryKey: ["routingPlan", row.routingPlanId],
    queryFn: () => fetchRoutingPlan(String(row.routingPlanId)),
    enabled: !!row.routingPlanId,
  });

  const { data: operator } = useQuery({
    queryKey: ["operator", row.operatorId],
    queryFn: () => fetchOperator(Number(row.operatorId)),
    enabled: !!row.operatorId,
  });

  const { data: country } = useQuery({
    queryKey: ["country", operator?.countryId],
    queryFn: () => fetchCountry(Number(operator?.countryId)),
    enabled: !!operator?.countryId,
  });

  const pills = [
    { label: "Routing Plan", value: routingPlan?.name },
    { label: "Country", value: country?.name },
    { label: "Operator", value: operator?.name },
    ...(total === undefined
      ? []
      : [{ label: "Instructions", value: String(total) }]),
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        {pills.map((pill) => (
          <Typography
            key={pill.label}
            component="span"
            variant="body2"
            sx={{
              bgcolor: COLORS.goldChipBackground,
              color: COLORS.GOLD,
              border: `1px solid ${COLORS.goldChipBorder}`,
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: 500,
              lineHeight: "28px",
              padding: "0 14px",
              cursor: "default",
            }}
          >
            {`${pill.label}: ${pill.value ?? "-"}`}
          </Typography>
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {total === 1
          ? "This is the only instruction configured for this routing plan and operator."
          : "Every instruction configured for this routing plan and operator is listed below. Cells highlighted in yellow are the values that differ between them; the row shaded in gold is the one you opened this from."}
      </Typography>
    </Box>
  );
}

function RelatedInstructionsContent<T extends RelatedInstructionsRow>({
  row,
  fields,
  fetchFunc,
}: Readonly<{
  row: RelatedInstructionsRow;
  fields: IField<T>[];
  fetchFunc: RelatedFetchFunc<T>;
}>) {
  const routeFilters = {
    routingPlanId: row.routingPlanId,
    operatorId: row.operatorId,
  } as Params<T>;

  // Same parameters the table itself requests, so this shares its cache entry
  // instead of firing a second call.
  const { data } = fetchFunc({
    ...routeFilters,
    page: 0,
    size: 20,
    sort: "",
  });

  return (
    <Box sx={{ width: "100%" }}>
      <RouteSummary row={row} total={data?.totalElements} />
      <Box sx={{ mt: 2 }}>
        <CustomTable<T>
          fullBleedMobile={false}
          filters={routeFilters}
          fields={fields}
          fetchFunc={fetchFunc}
          highlightDiff={true}
          diffScope="all"
          shadeRow={(item: T) => String(item.id) === String(row.id)}
          shadeBg="rgba(163, 148, 85, 0.18)"
          shadeColor={COLORS.typographyColor}
        />
      </Box>
    </Box>
  );
}

function RelatedInstructionsDialog<T extends RelatedInstructionsRow>({
  open,
  onClose,
  row,
  title,
  fields,
  fetchFunc,
}: Readonly<RelatedInstructionsDialogProps<T>>) {
  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      mode="related-instructions"
      title={title}
      fields={
        row
          ? [
              {
                type: "custom",
                fullWidth: true,
                component: (
                  <RelatedInstructionsContent<T>
                    row={row}
                    fields={fields}
                    fetchFunc={fetchFunc}
                  />
                ),
              },
            ]
          : []
      }
      buttons={[]}
    />
  );
}

export default RelatedInstructionsDialog;
